import React, { useEffect, useState } from 'react'
import { connect, useDispatch } from 'react-redux'
import { useParams, useHistory } from 'react-router-dom'
import ComplaintMessages from './ComplaintMessagesDetail'
import { AppState } from '../../../../reducers'
import { ComplaintDetailState } from '../../../../reducers/complaint/complaintDetail'
import { useTranslation } from 'react-i18next'
import { Paper, Box, Typography, Button, Table, TableCell, TableHead, TableRow, TableBody, CircularProgress, IconButton } from '@mui/material';
import { AttachFile, ChangeCircleOutlined, FlakyOutlined, KeyboardReturnOutlined } from '@mui/icons-material';
import moment from 'moment';
import { fetchComplaint } from '../../../../actions/complaint/complaintDetail'
import { fetchComplaintMessages } from '../../../../actions/complaint/complaintMessage'
import { ComplaintMessageState } from '../../../../reducers/complaint/complaintMessage'
import { SessionState, isAdmin, isClient, isHeadSales, isStaff } from '../../../../reducers/auth/session'
import { receiptComplaintTypeToDescription } from '../../../GeneralDesc'
import { ISTableHeaderCell, ISTableRow } from '../../../Utils/ISTable'
import ComplaintMessage from '../../../../models/ComplaintMessage'
import { upsertComplaintMessage } from '../../../../actions/complaint/complaintMessageUpsert'
import { complaintCloseReasonToDescription } from '..'
import Complaint from '../../../../models/Complaint'
import { upsertComplaint } from '../../../../actions/complaint/complaintUpsert'
import { useThemeContext } from '../../../../theme/ThemeContext'
import { downloadComplaintMessageFile } from '../../../../actions/complaint/complaintMessageDownloadFile'
import { roleToURLPrefix } from '../../../../App'
import { PERSON } from '../../../../models/User'

interface Props {
  complaint: ComplaintDetailState
  messages: ComplaintMessageState
  session: SessionState
}

const ComplaintDetail: React.FC<Props> = ({ complaint, messages, session }) => {
  const dispatch = useDispatch()
  const params = useParams<{ complaintId: string, viewClientInfo: string }>()
  const history = useHistory()
  const { t } = useTranslation(['complaint', 'others'])
  const { theme } = useThemeContext()

  useEffect(() => {
    dispatch(fetchComplaint(params.complaintId))
    dispatch(fetchComplaintMessages(params.complaintId))
  }, [dispatch, params.complaintId, history.location.search])

  const updateStatusComplaintMessage = async (complaintMessage: ComplaintMessage | undefined, complaint: Complaint, updateStatusComplaint: boolean = false) => {
    let update = false
    try {
      if (updateStatusComplaint && complaint.status === 'U') {
        complaint.status = 'R'
        await dispatch(upsertComplaint(complaint))
      }
      if (complaintMessage !== undefined) {
        if ((isHeadSales(session) || isStaff(session)) &&
          complaint.assignedToId === session.personId &&
          complaintMessage.ownerId === complaint.ownerOpenId &&
          complaintMessage.status === 'U') {
          complaintMessage.status = 'R'
          update = true
        }
        if (isHeadSales(session) &&
          complaint.assignedToId !== session.personId) {
          update = false
        }

        if (isStaff(session) &&
          complaint.assignedToId !== session.personId) {
          update = false
        }

        if (isClient(session) &&
          complaint.ownerOpenId === session.personId &&
          complaintMessage.ownerId !== session.personId &&
          complaintMessage.status === 'U') {
          complaintMessage.status = 'R'
          update = true
        }
        if (update) {
          await dispatch(upsertComplaintMessage(complaintMessage))
          await dispatch(fetchComplaintMessages(params.complaintId))
        }
      }
    }
    catch {
    }
  }


  const HeaderDetail: React.FC = () => {
    const [downloading, setDownloading] = useState<string>('')

    const downloadFileComplaintMessage = async (complaint: Complaint, fileId: string, fileName: string) => {
      if (complaint.fileId_1 && complaint.id) {
        try {
          setDownloading(fileId)
          await dispatch(downloadComplaintMessageFile(complaint.id, fileId, undefined,
            (file: any) => {

              const url = URL.createObjectURL(new Blob([file], { type: 'application/pdf' }))
              const link = document.createElement('a');
              link.href = url;
              link.setAttribute(
                'download',
                fileName ? fileName : `${complaint.message}.pdf`
              );
              document.body.appendChild(link);
              // Start download
              link.click();

              URL.revokeObjectURL(url);
            }))
          setDownloading('')
        }
        catch {
        }
      }
    }


    return (
      <Paper elevation={2} sx={{ margin: "10px", padding: "2px 2px 2px 2px", bgcolor: theme.palette.success.main }}>
        <Paper elevation={2}
          style={{
            padding: "15px 15px 15px 15px", display: "flex", flexDirection: "column",
            textAlign: "justify", justifyContent: "space-around",
            borderRadius: "4px", margin: "4px"
          }}>
          {(complaint.complaint?.receiptType_1 || complaint.complaint?.receiptType_2 || complaint.complaint?.receiptType_3 ||
            complaint.complaint?.fileId_1 || complaint.complaint?.fileId_2 || complaint.complaint?.fileId_3) &&
            <Box display='flex' flexDirection="row" justifyContent="space-between" alignItems="flex-start">
              <Box display='flex' flexDirection="row" justifyContent="flex-start" alignItems="flex-start">
                {complaint.complaint &&
                  <Box display="flex" flexDirection="column" alignItems="flex-start" >
                    <Typography fontWeight="bold" variant="h6">{t('id')}:&nbsp;&nbsp;</Typography>
                    <Typography fontWeight="bold" variant="h6">{t('dateOpen')}:&nbsp;&nbsp;</Typography>
                    <Typography fontWeight="bold" variant="h6">{t('status')}:&nbsp;&nbsp;</Typography>
                    <Typography fontWeight="bold" variant="h6">{t('category')}:&nbsp;&nbsp;</Typography>
                    <Typography fontWeight="bold" variant="h6">{t('type')}:&nbsp;&nbsp;</Typography>
                    {complaint.complaint.dateClose && <Typography fontWeight="bold" variant="h6">{t('closeReason')}:&nbsp;&nbsp;</Typography>}
                    {complaint.complaint.dateClose && <Typography fontWeight="bold" variant="h6">{t('closeResolution')}:&nbsp;&nbsp;</Typography>}
                  </Box>}

                {complaint.complaint &&
                  <Box display="flex" flexDirection="column" alignItems="flex-start" >
                    <Typography variant="h6">{complaint.complaint.id}</Typography>
                    <Typography variant="h6">{moment(complaint.complaint.dateOpen).tz("America/Argentina/Buenos_Aires").format('DD/MM/YYYY')}</Typography>
                    <Typography variant="h6">{complaint.complaint.dateClose ? `${t('closedOn')} ${moment(complaint.complaint.dateClose).tz("America/Argentina/Buenos_Aires").format('DD/MM/YYYY')}` : t('active')}</Typography>
                    <Typography variant="h6">{complaint.complaint.category}</Typography>
                    <Typography variant="h6">{complaint.complaint.type}</Typography>
                    {complaint.complaint.dateClose && complaint.complaint.closeReason && <Typography variant="h6">{t(complaintCloseReasonToDescription(complaint.complaint.closeReason))}</Typography>}
                    {complaint.complaint.dateClose && <Typography variant="h6">{complaint.complaint.closeResolution}</Typography>}

                  </Box>
                }
              </Box>
              {(complaint.complaint?.fileId_1 || complaint.complaint?.fileId_2 || complaint.complaint?.fileId_3) &&
                <Box display='flex' flexDirection="row" justifyContent="flex-end" alignItems="center">
                  <Box display='flex' flexDirection="column" justifyContent="flex-end" alignItems="center">
                    <Table style={{ whiteSpace: "nowrap", overflow: "hidden" }} size="small" >
                      <TableHead>
                        <TableRow selected={true}>
                          <ISTableHeaderCell>{t("files")}</ISTableHeaderCell>
                          <ISTableHeaderCell></ISTableHeaderCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {complaint.complaint?.fileId_1 &&
                          <ISTableRow key={1} color={'default'}>
                            <TableCell>
                              <Typography variant="body1">{complaint.complaint?.fileName_1}</Typography>
                            </TableCell>
                            <TableCell>
                              <IconButton color="secondary"
                                onClick={(e) => { e.stopPropagation(); complaint.complaint?.fileId_1 && complaint.complaint?.fileName_1 && downloadFileComplaintMessage(complaint.complaint, complaint.complaint?.fileId_1, complaint.complaint?.fileName_1) }}>
                                {downloading === complaint.complaint?.fileId_1 ? <CircularProgress size={15} /> : <AttachFile fontSize="small" />}
                              </IconButton>
                            </TableCell>
                          </ISTableRow>
                        }
                        {complaint.complaint?.fileId_2 &&
                          <ISTableRow key={2} color={'default'}>
                            <TableCell>
                              <Typography variant="body1">{complaint.complaint?.fileName_2}</Typography>
                            </TableCell>
                            <TableCell>
                              <IconButton color="secondary"
                                onClick={(e) => { e.stopPropagation(); complaint.complaint?.fileId_2 && complaint.complaint?.fileName_2 && downloadFileComplaintMessage(complaint.complaint, complaint.complaint?.fileId_2, complaint.complaint?.fileName_2) }}>
                                {downloading === complaint.complaint?.fileId_2 ? <CircularProgress size={15} /> : <AttachFile fontSize="small" />}
                              </IconButton>
                            </TableCell>
                          </ISTableRow>
                        }
                        {complaint.complaint?.fileId_3 &&
                          <ISTableRow key={3} color={'default'}>
                            <TableCell>
                              <Typography variant="body1">{complaint.complaint?.fileName_3}</Typography>
                            </TableCell>
                            <TableCell>
                              <IconButton color="secondary"
                                onClick={(e) => { e.stopPropagation(); complaint.complaint?.fileId_3 && complaint.complaint?.fileName_3 && downloadFileComplaintMessage(complaint.complaint, complaint.complaint?.fileId_3, complaint.complaint?.fileName_3) }}>
                                {downloading === complaint.complaint?.fileId_3 ? <CircularProgress size={15} /> : <AttachFile fontSize="small" />}
                              </IconButton>
                            </TableCell>
                          </ISTableRow>
                        }
                      </TableBody>
                    </Table>
                  </Box>
                </Box>}

              <Box display="flex" alignItems="center">
                <Table style={{ whiteSpace: "nowrap", overflow: "hidden" }} size="small" >
                  <TableHead>
                    <TableRow selected={true}>
                      <ISTableHeaderCell>{t("receipt")}</ISTableHeaderCell>
                      <ISTableHeaderCell>{t('receiptId')}</ISTableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>

                    {complaint.complaint?.receiptType_1 &&
                      <ISTableRow key={1} color={'default'}>
                        <TableCell><Typography variant="body1">{t(receiptComplaintTypeToDescription(complaint.complaint?.receiptType_1))}&nbsp;&nbsp;</Typography></TableCell>
                        <TableCell><Typography variant="body1">{complaint.complaint.receiptId_1}</Typography></TableCell>
                      </ISTableRow>
                    }
                    {complaint.complaint?.receiptType_2 &&
                      <ISTableRow key={2} color={'default'}>
                        <TableCell><Typography variant="body1">{t(receiptComplaintTypeToDescription(complaint.complaint?.receiptType_2))}&nbsp;&nbsp;</Typography></TableCell>
                        <TableCell><Typography variant="body1">{complaint.complaint.receiptId_2}</Typography></TableCell>
                      </ISTableRow>
                    }
                    {complaint.complaint?.receiptType_3 &&
                      <ISTableRow key={3} color={'default'}>
                        <TableCell><Typography variant="body1">{t(receiptComplaintTypeToDescription(complaint.complaint?.receiptType_3))}&nbsp;&nbsp;</Typography></TableCell>
                        <TableCell><Typography variant="body1">{complaint.complaint.receiptId_3}</Typography></TableCell>
                      </ISTableRow>
                    }
                  </TableBody>
                </Table>
              </Box>
            </Box>}

          { /*(complaint.complaint?.fileId_1 || complaint.complaint?.fileId_2 || complaint.complaint?.fileId_3) &&
          <Box display='flex' flexDirection="row" justifyContent="flex-end" alignItems="center">
            <Box display='flex' flexDirection="column" justifyContent="flex-end" alignItems="center">
              <Table style={{ whiteSpace: "nowrap", overflow: "hidden" }} size="small" >
                <TableHead>
                  <TableRow selected={true}>
                    <ISTableHeaderCell>{t("files")}</ISTableHeaderCell>
                    <ISTableHeaderCell></ISTableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {complaint.complaint?.fileId_1 &&
                    <ISTableRow key={1} color={'default'}>
                      <TableCell>
                        <Typography variant="body1">{complaint.complaint?.fileName_1}</Typography>
                        </TableCell>
                        <TableCell>
                        <IconButton color="secondary"
                          onClick={(e) => { e.stopPropagation(); complaint.complaint?.fileId_1 && complaint.complaint?.fileName_1 && downloadFileComplaintMessage(complaint.complaint, complaint.complaint?.fileId_1, complaint.complaint?.fileName_1) }}>
                          {downloading === complaint.complaint?.id ? <CircularProgress size={15} /> : <AttachFile fontSize="small" />}
                        </IconButton>
                      </TableCell>
                    </ISTableRow>
                  }
                  {complaint.complaint?.fileId_2 &&
                    <ISTableRow key={1} color={'default'}>
                      <TableCell>
                        <Typography variant="body1">{complaint.complaint?.fileName_2}</Typography>
                        </TableCell>
                        <TableCell>
                        <IconButton color="secondary"
                          onClick={(e) => { e.stopPropagation(); complaint.complaint?.fileId_2 && complaint.complaint?.fileName_2 && downloadFileComplaintMessage(complaint.complaint, complaint.complaint?.fileId_2, complaint.complaint?.fileName_2) }}>
                          {downloading === complaint.complaint?.id ? <CircularProgress size={15} /> : <AttachFile fontSize="small" />}
                        </IconButton>
                      </TableCell>
                    </ISTableRow>
                  }
                  {complaint.complaint?.fileId_3 &&
                    <ISTableRow key={1} color={'default'}>
                      <TableCell>
                        <Typography variant="body1">{complaint.complaint?.fileName_3}</Typography>
                        </TableCell>
                        <TableCell>
                        <IconButton color="secondary"
                          onClick={(e) => { e.stopPropagation(); complaint.complaint?.fileId_3 && complaint.complaint?.fileName_3 && downloadFileComplaintMessage(complaint.complaint, complaint.complaint?.fileId_3, complaint.complaint?.fileName_3) }}>
                          {downloading === complaint.complaint?.id ? <CircularProgress size={15} /> : <AttachFile fontSize="small" />}
                        </IconButton>
                      </TableCell>
                    </ISTableRow>
                  }
                </TableBody>
              </Table>
              </Box>
            </Box>
            */}
          {complaint.complaint &&
            <Box display="flex" flexDirection='row' >
              <Typography fontWeight="bold" variant="h6">{t('complaint')}:&nbsp;&nbsp;</Typography>
              <Typography style={{ wordBreak: "break-word", overflow: 'hidden', textOverflow: 'ellipsis' }} variant="h6">{complaint.complaint.message}</Typography>
            </Box>}
        </Paper>
      </Paper>
    )
  }

  const linkToComplaintEdit = (complaint: Complaint) => {
    const editUrl = isAdmin(session) ? `/admin/complaints/update/${complaint.id}` : `/${session.roles && roleToURLPrefix(session.roles.filter(role => role !== PERSON)[0])}/complaints/update/${complaint.id}`
    history.push(editUrl)
  }

  const linkToComplaintChangeCategory = (complaint: Complaint) => {
    const reassignUrl = isAdmin(session) ? `/admin/complaints/reassign/${complaint.id}` : `/${session.roles && roleToURLPrefix(session.roles.filter(role => role !== PERSON)[0])}/complaints/reassign/${complaint.id}`
    history.push(reassignUrl)
  }


  return (
    <div>
      <HeaderDetail />
      {!messages.isFetching &&
        <ComplaintMessages
          rows={(messages.complaintMessage || []).map(s => ({ data: s }))}
          loading={messages.isFetching}
          complaint={complaint.complaint!}
          pagination={messages.pagination || undefined}
          owner={session.personId || undefined}
          onClickChangeStatus={updateStatusComplaintMessage}
          viewClientInfo={params.viewClientInfo !== undefined}
          id={params.complaintId}
        />}
      <Box display="flex" justifyContent="space-between" alignItems="center">

        <Button color="secondary" variant="contained" sx={{ textTransform: "none", margin: "10px" }}
          startIcon={<KeyboardReturnOutlined />} onClick={() => history.goBack()}>{t("back", { ns: 'others' })}
        </Button>
        {complaint && complaint.complaint && !isClient(session) && !params.viewClientInfo && isHeadSales(session) &&
          <Button color="success" variant="contained" sx={{ textTransform: "none", margin: "10px" }}
            startIcon={<ChangeCircleOutlined />} onClick={(e) => { e.stopPropagation(); linkToComplaintChangeCategory(complaint.complaint!) }}>{t("reassign")}
          </Button>}
        {complaint && complaint.complaint && !isClient(session) && !params.viewClientInfo &&
          <Button color="success" variant="contained" sx={{ textTransform: "none", margin: "10px" }}
            startIcon={<FlakyOutlined />} onClick={(e) => { e.stopPropagation(); linkToComplaintEdit(complaint.complaint!) }}>{t("close")}
          </Button>}
      </Box>

      {messages.isFetching &&
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={25} />
        </div>
      }
    </div>
  )
}
/*
agregar otro boton como el flakyoutlined para que solo pueda entrar a editar el cambio de categoria (o de motivo en realidad y con esto cambiar la categoria) 
y que al hacerlo, limpie el asignado para que siga su curso. Ver de agregar un mensaje en el chat para que el que lo lea despues sepa que se lo derivaron
y no permitir en el cierre actual que se pueda modificar la categoria (hoy lo permite y esta mal porque solo debe permitir que se cierre el soporte)


hoy aunque no esté asignado a nadie (de la empresa) el click permite entrar y escribir en el chat (como hacen los botones pero sin botones )

ver de sacar la opcion de dar click sobre la linea para acceder al chat cuando NO soy el cliente. Si soy el cliente dejar que si pueda escrbir en el Chat
 Y dejar que solo se acceda al chat solo por el boton de seguimiento o Interno para los usuarios de la empresa
*/


const mapStateToProps = (state: AppState) => ({
  complaint: state.complaintDetail,
  messages: state.complaintMessage,
  session: state.session
})


export default connect<Props, {}, {}, AppState>(mapStateToProps, {})(ComplaintDetail);